import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { motion } from "framer-motion";

// Iconos
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import imgInfo from "../../static/media/contacto/apoyo.webp";

const C = {
  darkBlue: "#0C1F2F",
  navy: "#0057A8",
  gold: "#FFCD00",
  white: "#ffffff",
  cardBg: "rgba(255, 255, 255, 0.05)", // Fondo semitransparente para la "tabla"
};

const CONTACT_ITEMS = [
  {
    icon: <LocationOnIcon sx={{ color: C.gold, fontSize: 22 }} />,
    label: "Ubicación",
    lines: ["Quito, Ecuador."],
  },
  {
    icon: <PhoneIcon sx={{ color: C.gold, fontSize: 22 }} />,
    label: "Llámanos",
    lines: ["0999943920", "0998724998"],
  },
  {
    icon: <EmailIcon sx={{ color: C.gold, fontSize: 22 }} />,
    label: "Email",
    lines: [
      "ingenieria@electricagrm.com",
      "gerencia@electricagrm.com",
      "ventasce@electricagrm.com",
    ],
  },
];

export default function ContactoInfo() {
  return (
    <Box
      id="contacto-info"
      sx={{
        background: C.navy,
        // Incrementamos el pt en escritorio para bajar el título
        pt: { xs: "120px", lg: "130px" },
        pb: { xs: 8, lg: 8 },
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center", // Ayuda a centrar verticalmente
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 3, sm: 5, md: 6 },
          zIndex: 1,
          position: "relative",
          width: "100%",
        }}
      >
        {/* TÍTULO CENTRADO */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{
            mb: { xs: 6, lg: 6 }, // Reducimos margen para ahorrar espacio vertical
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              color: C.white,
              fontWeight: 750,
              fontSize: { xs: 28, sm: 34, md: 40 },
              letterSpacing: "-.02em",
              lineHeight: 1.1,
              mb: 1.5,
            }}
          >
            Estamos aquí para ayudarte
          </Typography>
          <Box
            sx={{
              width: "60px",
              height: "3px",
              background: C.gold,
              borderRadius: "2px",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            // Mantiene misma altura en ambos lados
            alignItems: { xs: "center", lg: "stretch" },
            gap: { xs: 6, md: 6 }, // Reducimos un poco el gap lateral
          }}
        >
          {/* LADO IZQUIERDO: LA "TABLA BONITA" DE CONTACTO */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            sx={{
              width: { xs: "100%", lg: "45%" },
              order: { xs: 2, lg: 1 },
              background: { xs: "transparent", lg: C.cardBg },
              backdropFilter: { lg: "blur(12px)" },
              borderRadius: "24px",
              border: { lg: "1px solid rgba(255, 255, 255, 0.1)" },
              // Reducimos el padding interno para compactar
              p: { xs: 0, lg: 4 },
              boxShadow: { lg: "0 20px 40px rgba(0,0,0,0.15)" },
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: C.gold,
                  fontWeight: 800,
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  fontSize: "0.85rem",
                  mb: 3,
                  textAlign: { xs: "center", lg: "left" },
                }}
              >
                Atención al Cliente
              </Typography>

              {/* Reducimos el gap entre iconos para ahorrar altura */}
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 4 }}
              >
                {CONTACT_ITEMS.map((item) => (
                  <Box
                    key={item.label}
                    sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
                  >
                    <Box
                      sx={{
                        width: 44, // Reducido levemente
                        height: 44,
                        borderRadius: "12px",
                        background: "rgba(255,205,0,0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          color: C.white,
                          fontWeight: 750,
                          fontSize: "1.1rem",
                          mb: 0.2,
                        }}
                      >
                        {item.label}
                      </Typography>
                      {item.lines.map((line) => (
                        <Typography
                          key={line}
                          sx={{
                            color: "rgba(255,255,255,0.85)",
                            fontSize: "0.95rem",
                            fontWeight: 500,
                            lineHeight: 1.5,
                          }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Redes Sociales al final de la tarjeta */}
            <Box>
              <Typography
                sx={{
                  color: C.white,
                  fontWeight: 700,
                  fontSize: "1rem",
                  mb: 1.5,
                  textAlign: { xs: "center", lg: "left" },
                }}
              >
                Síguenos en nuestras redes:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: { xs: "center", lg: "flex-start" },
                }}
              >
                {[
                  {
                    icon: <FacebookIcon fontSize="small" />,
                    link: "https://www.facebook.com/profile.php?id=100078693296652",
                  },
                  {
                    icon: <InstagramIcon fontSize="small" />,
                    link: "https://www.instagram.com/electrica_grm/",
                  },
                  {
                    icon: <WhatsAppIcon fontSize="small" />,
                    link: "https://api.whatsapp.com/send/?phone=593999943920&text=Hola+quiero+m%C3%A1s+informaci%C3%B3n&type=phone_number&app_absent=0",
                  },
                ].map((social, i) => (
                  <Box
                    key={i}
                    component="a"
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: C.white,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: C.gold,
                        color: C.darkBlue,
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 20px rgba(255,205,0,0.3)",
                      },
                    }}
                  >
                    {social.icon}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* LADO DERECHO: IMAGEN GRANDE Y ENMARCADA */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            sx={{
              width: { xs: "100%", lg: "55%" },
              display: "flex",
              justifyContent: "center",
              order: { xs: 1, lg: 2 },
              position: "relative",
            }}
          >
            {/* Marco decorativo dorado sutil detrás de la imagen */}
            <Box
              sx={{
                display: { xs: "none", lg: "block" },
                position: "absolute",
                top: "15px",
                right: "-15px",
                width: "100%",
                height: "100%",
                border: `2px solid ${C.gold}`,
                borderRadius: "24px",
                zIndex: 0,
                opacity: 0.5,
              }}
            />

            <Box
              component={motion.img}
              src={imgInfo}
              alt="Información de contacto"
              animate={{ y: [-6, 6, -6] }} // Movimiento flotante más suave
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              sx={{
                width: "100%",
                height: "100%",
                // Reducimos altura para evitar scroll en desktop
                maxHeight: { xs: "400px", lg: "440px" },
                objectFit: "cover",
                objectPosition: "top",
                borderRadius: "24px",
                boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
                position: "relative",
                zIndex: 1,
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
